import { useCallback, useMemo } from 'react';
import { Option, OptionKey } from '@common-fe/common-fe';

import { DefaultSortTypesEnum, ValuesMap } from '@/common/types';

import { useRolesListQuery } from '../queries';
import { IPermission, IPermissionGroup, NO_ACCESS_ROLE_ID, RoleLevel } from '../role.types';

const PER_PAGE = 1000;

interface Params {
  searchString?: string;
  organizationId?: string;
  roleLevels?: RoleLevel[];
  filterIds?: string[];
  roleLevel?: RoleLevel;
  currentRoleId?: string;
  permissions?: OptionKey[];
  syntheticElement?: IPermission;
}

const useRolesList = ({
  permissions,
  searchString,
  currentRoleId,
  filterIds,
  organizationId,
  roleLevels,
  roleLevel,
  syntheticElement,
}: Params) => {
  
  const { permissionOptions: elevateRoles, isLoading: isElevateLoading } = useRolesListQuery({
    permissions: permissions || [],
    partnerIds: [],
    levelType: RoleLevel.Elevate,
    searchString: '',
    enabled: roleLevels && roleLevels.includes(RoleLevel.Elevate),
    sortBy: DefaultSortTypesEnum.ASC,
    page: 1,
    currentRoleId,
    perPage: PER_PAGE,
  });
  const { permissionOptions: globalRoles, isLoading: isGlobalLoading } = useRolesListQuery({
    permissions: permissions || [],
    partnerIds: [],
    searchString: '',
    levelType: RoleLevel.Global,
    enabled: roleLevels && roleLevels.includes(RoleLevel.Global),
    sortBy: DefaultSortTypesEnum.ASC,
    page: 1,
    currentRoleId,
    perPage: PER_PAGE,
  });

  const { permissionOptions: partnerRoles, isLoading: isPartnerLoading } = useRolesListQuery({
    permissions: permissions || [],
    partnerIds: organizationId ? [organizationId] : [],
    searchString: '',
    enabled:
     roleLevels && roleLevels.includes(RoleLevel.Partner),
    levelType: RoleLevel.Partner,
    currentRoleId,
    sortBy: DefaultSortTypesEnum.ASC,
    page: 1,
    perPage: PER_PAGE,
  });
  const isLoading = useMemo(() => {
    return isElevateLoading || isGlobalLoading || isPartnerLoading;
  }, [isElevateLoading, isGlobalLoading, isPartnerLoading]);

  const getFilteredList = useCallback(
    (options: IPermission[]) => {
      const filteredList = options.filter((item) => (filterIds ? !filterIds.includes(item.id) : true));
      if (!searchString) {
        return filteredList;
      }
      return filteredList.filter((item) => item.name.toLowerCase().includes(searchString.toLowerCase()));
    },
    [filterIds, searchString]
  );
  const getItemsWithSyntheticItem = useCallback((items: IPermission[]): IPermission[] => {
    if(syntheticElement) {
      return [syntheticElement, ...items];
    } 
    return items;
    
  },[syntheticElement]);

  const filteredElevateRoles = useMemo(() => {
    if(roleLevel === RoleLevel.Elevate) {
      return getFilteredList(getItemsWithSyntheticItem(elevateRoles)); 
    }
    return getFilteredList(elevateRoles);
  }, [roleLevel, getFilteredList, elevateRoles, getItemsWithSyntheticItem]);

  const filteredGlobalRoles = useMemo(() => {
    if(roleLevel === RoleLevel.Global) {
      return getFilteredList(getItemsWithSyntheticItem(globalRoles)); 
    }
    return getFilteredList(globalRoles);
  }, [getFilteredList, getItemsWithSyntheticItem, globalRoles, roleLevel]);

  const filteredPartnerRoles = useMemo(() => {
    if(roleLevel === RoleLevel.Partner) {
      return getFilteredList(getItemsWithSyntheticItem(partnerRoles)); 
    }
    return getFilteredList(partnerRoles);
  }, [getFilteredList, getItemsWithSyntheticItem, partnerRoles, roleLevel]);

  const allOptions = useMemo(
    () => [...elevateRoles, ...globalRoles, ...partnerRoles],
    [elevateRoles, globalRoles, partnerRoles]
  );
  const groups = useMemo<IPermissionGroup[]>(() => {
    const list: IPermissionGroup[] = [
      {
        id: RoleLevel.Elevate,
        name: 'Elevate',
        options: filteredElevateRoles,
      },
      {
        id: RoleLevel.Global,
        name: 'Global',
        options: filteredGlobalRoles,
      },
      {
        id: RoleLevel.Partner,
        name: 'Partner',
        options: filteredPartnerRoles,
      },
    ];
    return list;
  }, [filteredElevateRoles, filteredGlobalRoles, filteredPartnerRoles]);
  const dropdownOptions = useMemo<Option[]>(() => {
    const formattedList =  allOptions.map((option) => ({
      key: option.id,
      title: option.name,
      value: option.name,
    }));
    const filteredList = formattedList.filter((item) => item.key !== NO_ACCESS_ROLE_ID);
    return filteredList;
  }, [allOptions]);


  const roleLevelGroups = useMemo<IPermissionGroup[]>(() => {
    let resultList: IPermissionGroup[] = [];
    if (filteredElevateRoles && filteredElevateRoles.length) {
      resultList =  [
        ...resultList,
        {
          id: RoleLevel.Elevate,
          name: 'Elevate',
          options: filteredElevateRoles,
        },
      ];
    }

    if (filteredGlobalRoles && filteredGlobalRoles.length) {
      resultList =  [
        ...resultList,
        {
          id: RoleLevel.Global,
          name: 'Global',
          options: filteredGlobalRoles,
        },
      ];
    } 
   
    if (filteredPartnerRoles && filteredPartnerRoles.length) {
      resultList =  [
        ...resultList,
        {
          id: RoleLevel.Partner,
          name: 'Partner',
          options: filteredPartnerRoles,
        },
      ];
    }  
  
    return resultList;
  }, [filteredElevateRoles, filteredGlobalRoles, filteredPartnerRoles]);

  const roleLevelOptionsMap = useMemo(() => {
    if (isLoading) return {};
    const currentMap: ValuesMap<IPermission> = {};

    allOptions.forEach((option) => {
      currentMap[option.key as string] = option;
    });
    return currentMap;
  }, [allOptions, isLoading]);

  const roleLevelOptions = useMemo(() => {
    const list =  [...elevateRoles, ...globalRoles, ...partnerRoles];
    return list;
  }, [elevateRoles, globalRoles, partnerRoles]);

  return {
    roleLevelOptionsMap,
    roleLevelGroups,
    roleLevelOptions,
    dropdownOptions,
    options: allOptions,
    isLoading,
    groups,
  };
};

export default useRolesList;
